import { type IModel, Metamodel } from 'shell-models-lib';
import { CopySource } from 'app/model/pipeline/copysource/copysource';
import { head, isArray } from 'lodash';
import { CopySink } from 'app/model/pipeline/copysink/copysink';
import { SqlCopySource } from 'app/model/pipeline/copysource/sql.copysource';
import { SqlSink } from 'app/model/pipeline/copysink/sql.copysink';
import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

const getCopySourceModelsFromDatasetType = (datasetType: string): IModel[] =>
    Metamodel.models().filter((model) => {
        if (isAncestor(CopySource.MODEL, model)) {
            const datasetModelTag = model.tag('datasetModel');
            // A copy source may be mapped to multiple dataset types
            if (datasetModelTag != null) {
                if (
                    isArray(datasetModelTag)
                        ? (datasetModelTag as IModel[]).some((m) => m.name === datasetType)
                        : (datasetModelTag as IModel).name === datasetType
                ) {
                    if (model.name === Metamodel.modelFor(SqlCopySource).name) {
                        // use separate copy source model for sqlv2
                        return false;
                    }
                    return true;
                }
            }
        }
        return false;
    });

const getCopySinkModelsFromDatasetType = (datasetType: string): IModel[] =>
    Metamodel.models().filter((model) => {
        if (isAncestor(CopySink.MODEL, model)) {
            const datasetModelTag = model.tag('datasetModel');
            // A copy sink may be mapped to multiple dataset types
            if (datasetModelTag != null) {
                if (
                    isArray(datasetModelTag)
                        ? (datasetModelTag as IModel[]).some((m) => m.name === datasetType)
                        : (datasetModelTag as IModel).name === datasetType
                ) {
                    if (model.name === Metamodel.modelFor(SqlSink).name) {
                        // use separate copy source model for sqlv2
                        return false;
                    }
                    return true;
                }
            }
        }
        return false;
    });

export const getCopySourceModelFromDatasetAndLinkedServiceType = (
    datasetType: string,
    linkedServiceConfig?: ILinkedServiceTypeMetadata
): IModel => {
    const availableCopySourceModels = getCopySourceModelsFromDatasetType(datasetType);
    let result: IModel | undefined;
    if (availableCopySourceModels.length === 1) {
        result = availableCopySourceModels[0];
    } else if (
        availableCopySourceModels.length > 1 &&
        linkedServiceConfig?.copySourceTypes.length >= 1
    ) {
        result = Metamodel.modelByName(linkedServiceConfig.copySourceTypes[0]);
    }
    return result;
};

/**
 * In Fabric, our object may pass across iframes, so we can't use the object itself to compare. Use `name` instead.
 * @param {IModel} parentModel Parent model
 * @param {IModel} childModel Child model
 */
export const isAncestor = (parentModel: IModel, childModel: IModel): boolean => {
    while (childModel.superModel) {
        if (childModel.superModel.name === parentModel.name) {
            return true;
        }
        childModel = childModel.superModel;
    }
    return false;
};

export const getCopySinkModelFromDatasetType = (datasetType: string): IModel =>
    head(getCopySinkModelsFromDatasetType(datasetType));
